class StickyAddToCart extends HTMLElement {
    constructor() {
      super();
      this.stickyClass = 'show';
      this.position = this.dataset.position || 'bottom';
      
      // Bind handlers once to preserve context
      this.onScrollHandler = this.handleScroll.bind(this);
      this.addToCartHandler = this.handleAddToCart.bind(this);
      this.variantChangeHandler = this.handleVariantChange.bind(this);
      this.quantityChangeHandler = this.handleQuantityChange.bind(this);
      this.stickyVariantChangeHandler = this.handleStickyVariantChange.bind(this);
    }
  
  connectedCallback() {
    // Add event listeners
    window.addEventListener('scroll', this.onScrollHandler, { passive: true });
    
    // Button click handler
    const addToCartButton = this.querySelector('.product-form__submit');
    if (addToCartButton) {
      addToCartButton.addEventListener('click', this.addToCartHandler);
    }
    
    // Set up variant listeners
    this.setupVariantObserver();
    this.setupVariantChangeListeners();
    this.setupStickyVariantChangeListeners();
    
    // Set up quantity sync
    this.setupQuantitySync();
    
    // Initialize
    this.updateStickyBarVariant();
    this.handleScroll();
  }

  disconnectedCallback() {
    // Remove scroll listener
    window.removeEventListener('scroll', this.onScrollHandler);
    
    // Remove button listener
    const addToCartButton = this.querySelector('.product-form__submit');
    if (addToCartButton) {
      addToCartButton.removeEventListener('click', this.addToCartHandler);
    }
    
    // Remove variant listeners
    this.removeVariantListeners();
    this.removeStickyVariantListeners();
    
    // Remove quantity listeners
    this.removeQuantityListeners();
    
    // Disconnect observer
    if (this.variantObserver) {
      this.variantObserver.disconnect();
    }
  }
  
  setupVariantObserver() {
    // Use MutationObserver to watch variant ID changes
    const productFormComponent = document.querySelector('product-form-component');
    const mainForm = productFormComponent ? productFormComponent.querySelector('form[data-type="add-to-cart-form"]') : null;
    
    if (!mainForm) return;
    
    const variantIdInput = mainForm.querySelector('[name="id"]');
    if (!variantIdInput) return;
    
    this.variantObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          this.updateStickyBarVariant();
          break;
        }
      }
    });
    
    this.variantObserver.observe(variantIdInput, { 
      attributes: true, 
      attributeFilter: ['value'] 
    });
  }

  setupVariantChangeListeners() {
    // Listen on the product-form-component for variant changes
    const productFormComponent = document.querySelector('product-form-component');
    if (productFormComponent) {
      productFormComponent.addEventListener('change', this.variantChangeHandler);
    }
    
    // Also listen for common variant change events
    ['variant:change', 'variant:changed', 'property_select:change'].forEach(eventName => {
      document.addEventListener(eventName, this.variantChangeHandler);
    });
  }

  setupStickyVariantChangeListeners() {
    // Listen for changes in the sticky variant picker
    const stickyVariantPicker = this.querySelector('variant-picker');
    if (stickyVariantPicker) {
      stickyVariantPicker.addEventListener('change', this.stickyVariantChangeHandler);
    }
    
    // Also listen for clicks on sticky variant inputs
    const stickyVariantInputs = this.querySelectorAll('input[type="radio"], select');
    stickyVariantInputs.forEach(input => {
      input.addEventListener('change', this.stickyVariantChangeHandler);
      if (input instanceof HTMLInputElement && input.type === 'radio') {
        input.addEventListener('click', this.stickyVariantChangeHandler);
      }
    });
  }
  
  removeVariantListeners() {
    // Remove product form component listener
    const productFormComponent = document.querySelector('product-form-component');
    if (productFormComponent) {
      productFormComponent.removeEventListener('change', this.variantChangeHandler);
    }
    
    // Remove event listeners
    ['variant:change', 'variant:changed', 'property_select:change'].forEach(eventName => {
      document.removeEventListener(eventName, this.variantChangeHandler);
    });
  }

  removeStickyVariantListeners() {
    // Remove sticky variant picker listener
    const stickyVariantPicker = this.querySelector('variant-picker');
    if (stickyVariantPicker) {
      stickyVariantPicker.removeEventListener('change', this.stickyVariantChangeHandler);
    }
    
    // Remove sticky variant input listeners
    const stickyVariantInputs = this.querySelectorAll('input[type="radio"], select');
    stickyVariantInputs.forEach(input => {
      input.removeEventListener('change', this.stickyVariantChangeHandler);
      if (input instanceof HTMLInputElement && input.type === 'radio') {
        input.removeEventListener('click', this.stickyVariantChangeHandler);
      }
    });
  }
  
  setupQuantitySync() {
    const sectionId = this.dataset.sectionId;
    const mainQuantityInput = document.querySelector(`#Quantity-${sectionId}`);
    const stickyQuantitySelector = this.querySelector('quantity-selector-component');
    const stickyQuantityInput = stickyQuantitySelector ? stickyQuantitySelector.querySelector('input[type="number"]') : null;
    
    if (!(mainQuantityInput instanceof HTMLInputElement) || !(stickyQuantityInput instanceof HTMLInputElement)) return;
    
    // Set initial value
    stickyQuantityInput.value = mainQuantityInput.value;
    
    // Main quantity changes -> update sticky
    mainQuantityInput.addEventListener('change', this.quantityChangeHandler);
    
    // Sticky quantity changes -> update main
    this.stickyQuantityChangeHandler = () => {
      if (mainQuantityInput instanceof HTMLInputElement && stickyQuantityInput instanceof HTMLInputElement) {
        mainQuantityInput.value = stickyQuantityInput.value;
        mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };
    if (this.stickyQuantityChangeHandler) {
      stickyQuantityInput.addEventListener('change', this.stickyQuantityChangeHandler);
    }
    
    // Handle quantity buttons in sticky bar
    const stickyQuantityButtons = stickyQuantitySelector ? stickyQuantitySelector.querySelectorAll('button[name="minus"], button[name="plus"]') : [];
    this.stickyQuantityButtonHandler = () => {
      // Small delay to let the quantity input update
      setTimeout(() => {
        if (stickyQuantityInput instanceof HTMLInputElement && mainQuantityInput instanceof HTMLInputElement) {
          mainQuantityInput.value = stickyQuantityInput.value;
          mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }, 10);
    };
    
    stickyQuantityButtons.forEach(button => {
      if (button instanceof HTMLElement && this.stickyQuantityButtonHandler) {
        button.addEventListener('click', this.stickyQuantityButtonHandler);
      }
    });
  }
  
  removeQuantityListeners() {
    const sectionId = this.dataset.sectionId;
    const mainQuantityInput = document.querySelector(`#Quantity-${sectionId}`);
    const stickyQuantitySelector = this.querySelector('quantity-selector-component');
    const stickyQuantityInput = stickyQuantitySelector ? stickyQuantitySelector.querySelector('input[type="number"]') : null;
    
    if (mainQuantityInput instanceof HTMLInputElement) {
      mainQuantityInput.removeEventListener('change', this.quantityChangeHandler);
    }
    
    if (stickyQuantityInput instanceof HTMLInputElement && this.stickyQuantityChangeHandler) {
      // Remove the change listener
      stickyQuantityInput.removeEventListener('change', this.stickyQuantityChangeHandler);
    }
    
    // Remove button listeners
    const stickyQuantityButtons = stickyQuantitySelector ? stickyQuantitySelector.querySelectorAll('button[name="minus"], button[name="plus"]') : [];
    stickyQuantityButtons.forEach(button => {
      if (button instanceof HTMLElement && this.stickyQuantityButtonHandler) {
        button.removeEventListener('click', this.stickyQuantityButtonHandler);
      }
    });
  }

  /**
   * @param {Event} event
   */
  handleQuantityChange(event) {
    const stickyQuantitySelector = this.querySelector('quantity-selector-component');
    const stickyQuantityInput = stickyQuantitySelector ? stickyQuantitySelector.querySelector('input[type="number"]') : null;
    
    if (stickyQuantityInput instanceof HTMLInputElement && event && event.target && event.target instanceof HTMLInputElement) {
      stickyQuantityInput.value = event.target.value;
    }
  }

  /**
   * @param {Event} event
   */
  handleStickyVariantChange(event) {
    // Get the changed element from the sticky variant picker
    const changedElement = event.target;
    if (!changedElement || !(changedElement instanceof HTMLElement)) return;

    // Prevent default behavior
    event.stopPropagation();

    // Get the selected variant ID from the changed element
    let variantId = null;
    
    if (changedElement instanceof HTMLInputElement && changedElement.type === 'radio') {
      // For radio buttons, get variant ID from data attribute
      variantId = changedElement.dataset.variantId;
    } else if (changedElement instanceof HTMLSelectElement) {
      // For dropdowns, get variant ID from selected option
      const selectedOption = changedElement.options[changedElement.selectedIndex];
      variantId = selectedOption ? selectedOption.dataset.variantId : null;
    }

    // If we have a variant ID, update the main form
    if (variantId) {
      this.updateMainFormVariant(variantId);
    } else {
      // If no direct variant ID, we need to calculate it from all selected options
      this.calculateAndUpdateVariant();
    }
  }

  /**
   * Update the main form with a specific variant ID
   * @param {string} variantId
   */
  updateMainFormVariant(variantId) {
    const productFormComponent = document.querySelector('product-form-component');
    const mainForm = productFormComponent ? productFormComponent.querySelector('form[data-type="add-to-cart-form"]') : null;
    
    if (!mainForm) {
      console.error('Could not find main form');
      return;
    }

    const mainVariantInput = mainForm.querySelector('[name="id"]');
    if (mainVariantInput instanceof HTMLInputElement) {
      // Update the variant ID
      mainVariantInput.value = variantId;
      
      // Trigger change event on the input
      mainVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
      
      // Also trigger change on the form to update UI
      mainForm.dispatchEvent(new Event('change', { bubbles: true }));
      
      // Update sticky bar after a short delay to let the main form update
      setTimeout(() => {
        this.updateStickyBarVariant();
      }, 50);
    }
  }

  /**
   * Calculate variant ID from all selected options in sticky picker
   */
  calculateAndUpdateVariant() {
    const stickyVariantPicker = this.querySelector('variant-picker');
    if (!stickyVariantPicker) return;

    // Get product variants data from the JSON script tag
    const variantDataScript = stickyVariantPicker.querySelector('script[type="application/json"]');
    let productData = null;
    
    if (variantDataScript) {
      try {
        productData = JSON.parse(variantDataScript.textContent);
      } catch (e) {
        console.error('Failed to parse product variant data', e);
      }
    }

    // Get all selected option values in order
    /** @type {string[]} */
    const selectedOptions = [];
    
    // Get values from radio buttons (in order by option position)
    /** @type {Record<string, string>} */
    const radioGroups = {};
    const checkedRadios = stickyVariantPicker.querySelectorAll('input[type="radio"]:checked');
    checkedRadios.forEach(radio => {
      if (radio instanceof HTMLInputElement && radio.dataset.inputId) {
        const position = radio.dataset.inputId.split('-')[0];
        if (position) {
          radioGroups[position] = radio.value;
        }
      }
    });
    
    // Get values from dropdowns
    const selects = stickyVariantPicker.querySelectorAll('select');
    selects.forEach((select, index) => {
      if (select instanceof HTMLSelectElement && select.value) {
        // Dropdowns are typically indexed from 0
        selectedOptions[index] = select.value;
      }
    });
    
    // Merge radio values into selectedOptions array
    Object.keys(radioGroups).sort().forEach((position, index) => {
      const value = radioGroups[position];
      if (value) {
        selectedOptions[index] = value;
      }
    });

    // Try to find matching variant ID from currently selected inputs
    let matchingVariantId = null;
    
    // Check checked radios first
    for (const radio of checkedRadios) {
      if (radio instanceof HTMLInputElement && radio.dataset.variantId) {
        matchingVariantId = radio.dataset.variantId;
        break;
      }
    }
    
    // If not found, check selected dropdown options
    if (!matchingVariantId) {
      for (const select of selects) {
        if (select instanceof HTMLSelectElement) {
          const selectedOption = select.options[select.selectedIndex];
          if (selectedOption && selectedOption.dataset.variantId) {
            matchingVariantId = selectedOption.dataset.variantId;
            break;
          }
        }
      }
    }

    if (matchingVariantId) {
      this.updateMainFormVariant(matchingVariantId);
    } else if (productData && productData.id) {
      // Fallback to the current product data variant
      this.updateMainFormVariant(productData.id.toString());
    }
  }

  /**
   * Find the appropriate main variant picker when there are multiple ones
   * @returns {HTMLElement | null}
   */
  findMainVariantPicker() {
    const stickyVariantPicker = this.querySelector('variant-picker');
    if (!stickyVariantPicker) return null;
    
    // Look for variant pickers that are NOT in the sticky component
    const mainVariantPickers = document.querySelectorAll('variant-picker[data-template-product-match="true"]');
    
    for (const picker of mainVariantPickers) {
      if (picker instanceof HTMLElement && 
          !picker.closest('sticky-add-to-cart') && // Exclude sticky picker
          !picker.closest('product-card') && 
          !picker.closest('quick-add-dialog')) {
        return picker;
      }
    }
    
    // Fallback: find the one that's in the main product section
    for (const picker of mainVariantPickers) {
      if (picker instanceof HTMLElement && 
          picker.closest('[id*="shopify-section-product"]')) {
        return picker;
      }
    }
    
    return null;
  }
  
  handleVariantChange() {
    // Update the sticky bar immediately and again after a short delay
    this.updateStickyBarVariant();
    this.syncStickyVariantPicker();
    
    setTimeout(() => {
      this.updateStickyBarVariant();
      this.syncStickyVariantPicker();
    }, 50);
  }

  syncStickyVariantPicker() {
    const mainVariantPicker = this.findMainVariantPicker();
    const stickyVariantPicker = this.querySelector('variant-picker');
    
    if (!mainVariantPicker || !stickyVariantPicker) {
      if (!mainVariantPicker) {
        console.warn('Could not find main variant picker for sync');
      }
      return;
    }
    
    // Get all selected options from main picker
    const mainSelectedInputs = mainVariantPicker.querySelectorAll('input[type="radio"]:checked, select option[selected]');
    
    mainSelectedInputs.forEach(mainInput => {
      if (mainInput instanceof HTMLInputElement && mainInput.type === 'radio') {
        const optionValueId = mainInput.dataset.optionValueId;
        if (optionValueId) {
          const stickyInput = stickyVariantPicker.querySelector(`input[data-option-value-id="${optionValueId}"]`);
          if (stickyInput instanceof HTMLInputElement) {
            stickyInput.checked = true;
          }
        }
      } else if (mainInput instanceof HTMLOptionElement && mainInput.selected) {
        // Handle select options
        const selectElement = mainInput.closest('select');
        if (selectElement instanceof HTMLSelectElement) {
          const selectName = selectElement.name;
          const stickySelect = stickyVariantPicker.querySelector(`select[name="${selectName}"]`);
          if (stickySelect instanceof HTMLSelectElement) {
            stickySelect.value = selectElement.value;
          }
        }
      }
    });
  }

  updateStickyBarVariant() {
    const productFormComponent = document.querySelector('product-form-component');
    const mainForm = productFormComponent ? productFormComponent.querySelector('form[data-type="add-to-cart-form"]') : null;
    if (!mainForm) return;
    
    const variantIdInput = mainForm.querySelector('[name="id"]');
    if (!(variantIdInput instanceof HTMLInputElement)) return;
    
    // Update variant ID
    const stickyVariantIdInput = this.querySelector('[name="id"]');
    if (stickyVariantIdInput instanceof HTMLInputElement) {
      stickyVariantIdInput.value = variantIdInput.value;
    }
    
    this.updateVariantTitle();
    this.updateStickyBarPrice();
    this.updateAvailabilityStatus(mainForm);
  }
  
  updateVariantTitle() {
    const stickyFormVariantTitle = this.querySelector('.sticky-add-to-cart__variant');
    if (!stickyFormVariantTitle) return;
    
    // Reset previous content first
    stickyFormVariantTitle.textContent = '';
    
    // Try to get variant display from main product info
    const mainFormVariantTitle = document.querySelector('.product__variant-status');
    
    if (mainFormVariantTitle) {
      stickyFormVariantTitle.textContent = mainFormVariantTitle.textContent;
      return;
    }
    
    // Alternative: gather selected options
    /** @type {string[]} */
    const selectedOptions = [];
    
    // From dropdowns
    document.querySelectorAll('.product-form__input select').forEach(select => {
      if (select instanceof HTMLSelectElement && select.value) {
        selectedOptions.push(select.value);
      }
    });
    
    // From radio buttons
    document.querySelectorAll('.product-form__input input[type="radio"]:checked').forEach(radio => {
      if (radio instanceof HTMLInputElement) {
        selectedOptions.push(radio.value);
      }
    });
    
    // Display if we have options
    if (selectedOptions.length > 0) {
      const uniqueOptions = [...new Set(selectedOptions)];
      stickyFormVariantTitle.textContent = uniqueOptions.join(' / ');
    }
  }
  
  updateStickyBarPrice() {
    const mainPrice = document.querySelector('.price:not(.sticky-add-to-cart .price)');
    const stickyPrice = this.querySelector('.sticky-add-to-cart__price');
    
    if (mainPrice && stickyPrice) {
      // Clone price content to the sticky bar
      stickyPrice.innerHTML = '';
      const priceClone = mainPrice.cloneNode(true);
      stickyPrice.appendChild(priceClone);
    }
  }
  
  /**
   * @param {any} mainForm
   */
  updateAvailabilityStatus(mainForm) {
    const addToCartButton = mainForm.querySelector('button[type="submit"][name="add"]');
    const stickyAddToCartButton = this.querySelector('.product-form__submit');
    
    if (addToCartButton instanceof HTMLButtonElement && stickyAddToCartButton instanceof HTMLButtonElement) {
      // Update button state
      stickyAddToCartButton.disabled = addToCartButton.disabled;
      
      // Update button text - get the actual button text content
      const mainButtonTextSpan = addToCartButton.querySelector('.add-to-cart-text__content');
      const stickySpan = stickyAddToCartButton.querySelector('span');
      
      if (mainButtonTextSpan && stickySpan) {
        stickySpan.textContent = mainButtonTextSpan.textContent;
      }
    }
  }

  handleScroll() {
    // Find the actual add to cart button in the main product form
    const addToCartButton = document.querySelector('product-form-component .add-to-cart-button, product-form-component button[type="submit"][name="add"]');
    if (!(addToCartButton instanceof HTMLElement)) return;
    
    const stickyHeight = this.offsetHeight;
    const buttonRect = addToCartButton.getBoundingClientRect();
    const buttonBottom = buttonRect.bottom + window.scrollY;
    
    // Show sticky bar when the main ATC button is scrolled past
    const shouldStick = window.scrollY > buttonBottom;
    const isSticky = this.classList.contains(this.stickyClass);
    
    // Add/remove sticky class based on scroll position
    if (shouldStick && !isSticky) {
      this.style.minHeight = `${stickyHeight}px`;
      this.classList.add(this.stickyClass);
    } else if (!shouldStick && isSticky) {
      this.style.removeProperty('min-height');
      this.classList.remove(this.stickyClass);
    }
  }
  
  /**
   * @param {Event} event
   */
  handleAddToCart(event) {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    
    // Find the product-form-component (Shopify's custom element)
    const productFormComponent = document.querySelector('product-form-component');
    const mainForm = productFormComponent ? productFormComponent.querySelector('form[data-type="add-to-cart-form"]') : null;
    
    if (!mainForm) {
      console.error('Could not find main product form');
      return;
    }
    
    // Sync quantity and variant ID with main form BEFORE submitting
    this.syncFormData(mainForm);
    
    // Wait a tiny bit for the sync to complete, then trigger the main button
    setTimeout(() => {
      // Trigger the main add to cart button click to use Shopify's built-in handling
      const mainSubmitButton = mainForm.querySelector('button[type="submit"][name="add"]');
      if (mainSubmitButton instanceof HTMLButtonElement && !mainSubmitButton.disabled) {
        // Create and dispatch a proper click event
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        mainSubmitButton.dispatchEvent(clickEvent);
      }
    }, 10);
  }
  
  /**
   * @param {any} mainForm
   */
  syncFormData(mainForm) {
    const stickyQuantitySelector = this.querySelector('quantity-selector-component');
    const stickyQuantityInput = stickyQuantitySelector ? stickyQuantitySelector.querySelector('input[type="number"]') : null;
    const mainQuantityInput = document.querySelector(`#Quantity-${this.dataset.sectionId}`);
    
    if (stickyQuantityInput instanceof HTMLInputElement && mainQuantityInput instanceof HTMLInputElement) {
      mainQuantityInput.value = stickyQuantityInput.value;
      mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    
    // Sync variant ID
    const stickyVariantIdInput = this.querySelector('[name="id"]');
    const mainVariantIdInput = mainForm.querySelector('[name="id"]');
    
    if (stickyVariantIdInput instanceof HTMLInputElement && mainVariantIdInput instanceof HTMLInputElement) {
      mainVariantIdInput.value = stickyVariantIdInput.value;
    }
  }
}
  
  customElements.define('sticky-add-to-cart', StickyAddToCart);
  
  // Handle header visibility changes
  window.addEventListener("scroll", function() {
    const stickyAddToCart = document.querySelector('sticky-add-to-cart');
    if (!stickyAddToCart) return;
    
    // Handle header state
    const header = document.querySelector('#shopify-section-header');
    const stickyInner = document.querySelector('.sticky-inner-container');
    
    if (header?.classList.contains('shopify-section-header-hidden')) {
      stickyInner?.classList.add('show-my-class');
    } else {
      stickyInner?.classList.remove('show-my-class');
    }
  }); 